import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import "../../components/home/home.css";
import "./Products.css";
import ThreeBackground from "../../components/common/ThreeBackground";
import Icon from "../../components/common/Icon";
import collabizLogo from "../../assets/logos/collabiz.svg";
import BridgeDiagram from "../../components/common/BridgeDiagram";
import { products, digitalProducts, industrialPartner, ventures } from "../../Data/siteContent";

/* Real product screenshots. Keys match `shots` in siteContent.products. */
import shotCollabiz from "../../assets/work/collabiz-dashboard.webp";
import shotSales from "../../assets/work/datavista-sales.webp";
import shotInventory from "../../assets/work/datavista-inventory.webp";
import shotBuilder from "../../assets/work/datavista-builder.webp";
import shotAnalyst from "../../assets/work/ai-analyst-sap.webp";
import shotChatvista from "../../assets/work/chatvista-landing.webp";
import { useSeo, SEO } from "../../config/seo";

const SHOTS = {
  "collabiz-dashboard.webp": shotCollabiz,
  "datavista-sales.webp": shotSales,
  "datavista-inventory.webp": shotInventory,
  "datavista-builder.webp": shotBuilder,
  "ai-analyst-sap.webp": shotAnalyst,
  "chatvista-landing.webp": shotChatvista,
};

const ease = [0.21, 0.8, 0.25, 1];
const ACCENT = { cyan: "#34e6ff", electric: "#4f8cff", violet: "#8b5cf6", coffee: "#c98a52", green: "#16a34a" };

const Reveal = ({ children, delay = 0, className }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.6, delay, ease }}
  >
    {children}
  </motion.div>
);

export default function Products() {
  useSeo(SEO.products);
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.hash]);

  return (
    <main className="cv-home cv-products">
      {/* A. Hero */}
      <header className="cv-prod-hero">
        <ThreeBackground variant="constellation" />
        <div className="cv-prod-hero__inner">
          <motion.span className="cv-eyebrow" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
            <span className="dot" /> Our products
          </motion.span>
          <motion.h1 className="cv-prod-hero__title" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.08, ease }}>
            Software we built, run and{" "}
            <span className="cv-gradient-text">sell as products</span>.
          </motion.h1>
          <motion.p className="cv-prod-hero__sub" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.16, ease }}>
            Not concepts. These are in production with real businesses &mdash; an ERP platform,
            analytics over SAP, an assistant that answers off live data, and an embeddable chatbot.
          </motion.p>
          <motion.div className="cv-prod-hero__chips" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }}>
            {products.map((p) => (
              <a key={p.id} href={`#${p.id}`} className={`cv-chip cv-acc-${p.accent} cv-prod-hero__chip`}>
                <span className="cv-prod-hero__dot" /> {p.name}
              </a>
            ))}
          </motion.div>
        </div>
      </header>

      {/* B. Products */}
      {products.map((p, i) => (
        <ProductSection key={p.id} product={p} reverse={i % 2 === 1} />
      ))}

      {/* C. Digital products (not shipped yet) */}
      <DigitalProducts />

      {/* D. The studio */}
      <StudioSection />

      {/* E. Client & industrial partner */}
      <PartnerSection />

      {/* F. CTA */}
      <section className="cv-section cv-section--tight">
        <motion.div
          className="cv-prod-cta"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, ease }}
        >
          <div className="cv-prod-cta__glow" aria-hidden="true" />
          <h2>
            Want one of these on <span className="cv-gradient-text">your data</span>?
          </h2>
          <p>
            Tell us which systems you run and what you need out of them. We&rsquo;ll come back with
            what we&rsquo;d deploy, what we&rsquo;d build, and what it costs.
          </p>
          <div className="cv-prod-cta__actions">
            <Link to="/contactus" className="cv-btn">
              Start a conversation <Icon name="arrow" size={18} className="arrow" />
            </Link>
            <Link to="/service" className="cv-btn cv-btn--ghost">
              See our solutions
            </Link>
          </div>
        </motion.div>
      </section>
    </main>
  );
}

/* ---------- one product ---------- */
function ProductSection({ product, reverse }) {
  const accent = ACCENT[product.accent] || ACCENT.cyan;
  const shots = (product.shots || []).map((s) => SHOTS[s]).filter(Boolean);
  const external = product.href && product.href.endsWith(".html");

  return (
    <section id={product.id} className={`cv-pdt cv-acc-${product.accent}`} style={{ "--accent": accent }}>
      <div className="cv-pdt__glow" aria-hidden="true" />
      <div className={`cv-pdt__inner ${reverse ? "cv-pdt__inner--rev" : ""}`}>
        <div className="cv-pdt__text">
          <Reveal>
            <span className="cv-pdt__kicker">
              <Icon name={product.icon} size={15} /> {product.kicker}
              {product.status === "live" && <em className="cv-pdt__live">Live</em>}
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="cv-pdt__title">
              {product.logo && <img src={collabizLogo} alt="" className="cv-pdt__logo" />}
              {product.name}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="cv-pdt__body">{product.summary}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <ul className="cv-pdt__features">
              {product.features.map((f) => (
                <li key={f}>
                  <Icon name="check" size={15} />
                  {f}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.2}>
            {external ? (
              <a href={product.href} target="_blank" rel="noopener noreferrer" className="cv-btn cv-btn--violet">
                {product.ctaLabel} <Icon name="arrow" size={18} className="arrow" />
              </a>
            ) : (
              <Link to={product.href} className="cv-btn">
                {product.ctaLabel} <Icon name="arrow" size={18} className="arrow" />
              </Link>
            )}
          </Reveal>
        </div>

        <Reveal delay={0.1} className="cv-pdt__visual">
          {product.diagram === "bridge" ? (
            <BridgeDiagram />
          ) : (
          <div className="cv-pdt__shots">
            <img
              src={shots[0]}
              alt={`${product.name} — ${product.kicker}`}
              className="cv-pdt__shot"
              loading="lazy"
              width="1600"
              height="740"
            />
            {shots.length > 1 && (
              <div className="cv-pdt__thumbs">
                {shots.slice(1).map((src, i) => (
                  <img
                    key={src}
                    src={src}
                    alt={`${product.name} screenshot ${i + 2}`}
                    className="cv-pdt__shot cv-pdt__shot--thumb"
                    loading="lazy"
                    width="1600"
                    height="740"
                  />
                ))}
              </div>
            )}
          </div>
          )}
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- digital products (planned) ---------- */
function DigitalProducts() {
  return (
    <section className="cv-section cv-dp" id="digital">
      <div className="cv-dp__panel">
        <Reveal>
          <span className="cv-eyebrow cv-dp__eyebrow">
            <span className="dot" /> In the works
          </span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="cv-h2">{digitalProducts.heading}</h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="cv-lead">{digitalProducts.lead}</p>
        </Reveal>

        <div className="cv-dp__grid">
          {digitalProducts.items.map((it, i) => (
            <motion.div
              key={it.id}
              className="cv-dp__card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.07, ease }}
            >
              <span className="cv-dp__ic">
                <Icon name={it.icon} size={20} />
              </span>
              <h3>{it.name}</h3>
              <p>{it.body}</p>
            </motion.div>
          ))}
        </div>

        <Reveal delay={0.15}>
          <Link to={digitalProducts.cta.to} className="cv-btn cv-btn--sm cv-btn--ghost cv-dp__cta">
            {digitalProducts.cta.label} <Icon name="arrow" size={16} className="arrow" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- the studio ---------- */
function StudioSection() {
  const v = ventures.find((x) => x.id === "vistaai");
  if (!v) return null;
  return (
    <section className="cv-section cv-section--tight" id="vistaai">
      <div className="cv-studio">
        <div className="cv-studio__text">
          <Reveal>
            <span className="cv-pdt__kicker">
              <Icon name="brain" size={15} /> {v.kicker}
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="cv-h2">Everything else, built to order.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="cv-lead">
              The products above came out of client work. When none of them fit, we build the system
              that does &mdash; integrations, internal tools, portals, mobile, commerce, embedded and
              IoT.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="cv-studio__stats">
              {v.stats.map((s) => (
                <div key={s.l}>
                  <strong>{s.v}</strong>
                  <span>{s.l}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/service" className="cv-btn">
              Browse solutions <Icon name="arrow" size={18} className="arrow" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------- client & industrial partner ---------- */
function PartnerSection() {
  const p = industrialPartner;
  return (
    <section className="cv-section cv-section--tight" id="mbindustries">
      <div className="cv-partner cv-acc-green">
        <div className="cv-partner__text">
          <Reveal>
            <span className="cv-pdt__kicker">
              <Icon name={p.icon} size={15} /> {p.kicker}
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="cv-h2">
              {p.name} <span className="cv-partner__brand">&mdash; {p.brand}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="cv-lead">{p.body}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="cv-studio__stats">
              {p.stats.map((s) => (
                <div key={s.l}>
                  <strong>{s.v}</strong>
                  <span>{s.l}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <a href={p.external} target="_blank" rel="noopener noreferrer" className="cv-btn cv-btn--green">
              {p.cta.label} <Icon name="arrow" size={18} className="arrow" />
            </a>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
